'use client';

import styled from '@emotion/styled';
import { PropsWithChildren } from 'react';
import { Theme } from '@emotion/react';
import { useRouter, useSearchParams } from 'next/navigation';
import { Link as NextLink } from '../Link';
import { SpanHoverUnderline } from './Span';
import {
  DEVICE_SCREENS_MAX_STRING,
  DEVICE_SCREENS_MIN_STRING,
} from '../../lib/consts';
import SearchSVG from '../../public/img/search.svg';
import CloseSVG from '../../public/img/close.svg';
import { HistoryLastPage } from '../../models/history';
import { extractUtmParams } from '../../utils/utm';
import { withSuspense } from '../../utils/withSuspense';

export const Link = withSuspense(
  ({
    href = '',
    isBlank = false,
    children,
    ...restProps
  }: PropsWithChildren<{
    href: string;
    isBlank?: boolean;
    [restProps: string]: any;
  }>) => {
    let hrefWithUtm = null;
    const searchParams = useSearchParams();

    const utmParams = extractUtmParams(searchParams);
    if (utmParams.utmString) {
      if (href.includes('?')) {
        hrefWithUtm = `${href}&${utmParams.utmString}`;
      } else {
        hrefWithUtm = `${href}?${utmParams.utmString}`;
      }
    }

    if (isBlank) {
      return (
        <a
          href={hrefWithUtm ?? href}
          rel='noreferrer noopener'
          target='_blank'
          {...restProps}
          style={{
            cursor: 'pointer',
          }}
        >
          {children}
        </a>
      );
    }

    return (
      <NextLink
        href={hrefWithUtm ?? href}
        {...restProps}
        style={{
          cursor: 'pointer',
        }}
        aria-label='ссылка'
      >
        {children}
      </NextLink>
    );
  }
);

export const TransformLink = styled(Link)({
  '&:hover': { transform: 'scale(1.05)', transition: '0.3s' },
});

export function LinkLikeButton({
  href,
  children,
}: PropsWithChildren<{
  href: string;
}>) {
  return (
    <Link
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      css={{
        marginTop: 25,
        marginBottom: 50,
        width: 288,
        fontFamily: 'Golos',
        fontSize: 16,
        fontWeight: 500,
        lineHeight: 16,
        letterSpacing: '0em',
        border: 'none',
        cursor: 'pointer',
        textDecoration: 'none',
        color: '#fff',
        height: '2.56em',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '1.28em',
        padding: '0 1.6em',
        backgroundColor: '#ff4a2e',
        backgroundImage: 'linear-gradient(120deg, #ff4a2e 0%, #de2839 100%)',
        backgroundSize: '500% 500%',
        '&:hover': {
          color: '#fff',
        },
        [DEVICE_SCREENS_MAX_STRING.md]: {
          marginBottom: 0,
        },
      }}
      href={href}
    >
      {children}
    </Link>
  );
}

export function SearchLink({ theme }: { theme: Theme }) {
  return (
    <Link
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      css={{ 'span:hover': { color: theme.colors.white } }}
      href={['', 'search', ''].join('/')}
    >
      <SpanHoverUnderline
        css={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          opacity: '1',
          padding: '0',
          width: '36px',
          [DEVICE_SCREENS_MIN_STRING.lg]: {
            width: '100%',
            padding: '20px 15px 10px 15px',
          },
        }}
      >
        <SearchSVG width='20px' height='20px' />
        <span
          css={{
            display: 'none',
            [DEVICE_SCREENS_MIN_STRING.lg]: {
              display: 'inline',
            },
          }}
        >
          Поиск
        </span>
      </SpanHoverUnderline>
    </Link>
  );
}

export function CloseSearch({
  lastPage,
}: {
  lastPage: null | HistoryLastPage;
}) {
  const router = useRouter();

  return (
    <button
      type='button'
      onClick={() => {
        if (!lastPage || lastPage.type === 'error') {
          router.push('/');
        } else {
          router.back();
        }
      }}
    >
      <SpanHoverUnderline
        css={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          opacity: '1',
          padding: '0',
          width: '36px',
          [DEVICE_SCREENS_MIN_STRING.lg]: {
            width: '100%',
            padding: '20px 15px 10px 15px',
          },
        }}
      >
        <CloseSVG width='20px' height='20px' />
        <span
          css={{
            display: 'none',
            [DEVICE_SCREENS_MIN_STRING.lg]: {
              display: 'inline',
            },
          }}
        >
          Поиск
        </span>
      </SpanHoverUnderline>
    </button>
  );
}
